import { db } from './db';
import { decryptChunk } from './cryptoClient';
import { pushMachineEvent } from './machineLog';
import { chunkFingerprint } from './uploads';

const API_URL = '/api/tresor';

// Aus der signierten URL nur Host und Objektpfad lesen. Die URL selbst enthält die
// Signatur und gehört nie in ein Protokoll. Der Objektpfad lautet userId/uploadId/index
// und enthält bewusst keinen Dateinamen.
function urlFacts(url) {
  try {
    const parsed = new URL(url);
    return { targetHost: parsed.host, objectPath: parsed.pathname.replace(/^\//, '') };
  } catch {
    return { targetHost: '', objectPath: '' };
  }
}


// Herunterladen: bis zu 4 Bloecke gleichzeitig, Adressen gebuendelt (bis zu 8 je Anfrage). Reicht das
// Download-Budget fuer einen ganzen Stapel nicht, faellt der Browser auf einzelne Bloecke zurueck und
// nutzt den Rest genau aus; erst dann kommt die Meldung.
const DOWNLOAD_PARALLEL = 4;
const SIGN_BATCH = 8;

function egressError(info) {
  const err = new Error(info?.message || 'Monatliches Download-Limit erreicht.');
  err.code = 'EGRESS_LIMIT';
  return err;
}

function createDownloadUrlPool(authenticatedFetch, uploadId, totalChunks) {
  const cache = new Map(); // idx -> { url, until }
  let inflight = null;
  let batchOk = true;
  const fill = async (fromIdx) => {
    if (!batchOk) return;
    try {
      const res = await authenticatedFetch(`${API_URL}/upload/${uploadId}/chunks/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from: fromIdx, count: Math.min(SIGN_BATCH, totalChunks - fromIdx) })
      });
      if (!res.ok) { batchOk = false; return; } // z. B. 429 beim Stapel: ab jetzt einzeln
      const { urls = {}, expiresIn = 120 } = await res.json();
      const until = Date.now() + Math.max(10, expiresIn - 30) * 1000;
      for (const [i, url] of Object.entries(urls)) cache.set(Number(i), { url, until });
    } catch (_) { batchOk = false; }
  };
  const single = async (idx) => {
    const urlRes = await authenticatedFetch(`${API_URL}/upload/${uploadId}/chunk/${idx}`);
    if (urlRes.status === 429) throw egressError(await urlRes.json().catch(() => ({})));
    if (!urlRes.ok) throw new Error('CHUNK_DOWNLOAD_FAILED');
    const { url } = await urlRes.json();
    if (!url) throw new Error('CHUNK_DOWNLOAD_FAILED');
    return url;
  };
  return {
    async take(idx) {
      let e = cache.get(idx);
      if (!(e && e.until > Date.now()) && batchOk) {
        if (!inflight) inflight = fill(idx).finally(() => { inflight = null; });
        await inflight;
        e = cache.get(idx);
        if (!(e && e.until > Date.now()) && batchOk) { // anderer Stapel lief gerade: noch einmal ab idx
          if (!inflight) inflight = fill(idx).finally(() => { inflight = null; });
          await inflight;
          e = cache.get(idx);
        }
      }
      if (e && e.until > Date.now()) { cache.delete(idx); return e.url; }
      return single(idx);
    }
  };
}

/** Einen Block holen und entschluesseln. Liefert Kryptotext (fuer den Zwischenspeicher) und Klartext. */
async function fetchChunk(pool, activeKey, fileMeta, i) {
  const { totalChunks, encryptionId } = fileMeta;
  const url = await pool.take(i);
  const facts = urlFacts(url);
  pushMachineEvent({
    kind: 'sign', direction: 'download', fileName: fileMeta.name,
    chunkIndex: i, totalChunks, sameOrigin: true, authenticated: true, bodyBytes: 0,
    ...facts
  });
  // Direkt vom Objektspeicher, ohne Anmeldedaten. Harte Zeitgrenze, damit nichts ewig haengt.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 60000);
  let encrypted;
  const getStart = performance.now();
  try {
    const chunkRes = await fetch(url, { signal: controller.signal });
    if (!chunkRes.ok) throw new Error('CHUNK_DOWNLOAD_FAILED');
    encrypted = await chunkRes.arrayBuffer();
  } finally {
    clearTimeout(timer);
  }
  pushMachineEvent({
    kind: 'get', fileName: fileMeta.name, chunkIndex: i, totalChunks,
    sameOrigin: false, authenticated: false, cipherBytes: encrypted.byteLength,
    fingerprint: chunkFingerprint(new Uint8Array(encrypted)),
    ms: performance.now() - getStart, ...facts
  });
  const decStart = performance.now();
  const plain = await decryptChunk(encrypted, activeKey, encryptionId, i);
  pushMachineEvent({
    kind: 'decrypt', target: 'cloud', fileName: fileMeta.name, chunkIndex: i, totalChunks,
    cipherBytes: encrypted.byteLength, plainBytes: plain.byteLength,
    ms: performance.now() - decStart
  });
  return { encrypted, plain: new Uint8Array(plain) };
}

/**
 * Download encrypted chunks from cloud, decrypt, optionally cache in IndexedDB.
 */
export async function fetchAndDecryptFile(authenticatedFetch, activeKey, fileMeta, { cacheLocally = true } = {}) {
  const { uploadId, totalChunks, encryptionId, originalType } = fileMeta;
  if (!uploadId || !totalChunks || !encryptionId) {
    throw new Error('NO_CLOUD_REFERENCE');
  }
  const pool = createDownloadUrlPool(authenticatedFetch, uploadId, totalChunks);
  const plainParts = new Array(totalChunks);
  const cipherParts = new Array(totalChunks);
  let next = 0;
  const worker = async () => {
    for (;;) {
      const i = next++;
      if (i >= totalChunks) return;
      const { encrypted, plain } = await fetchChunk(pool, activeKey, fileMeta, i);
      plainParts[i] = plain;
      if (cacheLocally) cipherParts[i] = encrypted;
    }
  };
  await Promise.all(Array.from({ length: Math.min(DOWNLOAD_PARALLEL, totalChunks) }, worker));

  const blob = new Blob(plainParts, { type: originalType || 'application/octet-stream' });
  if (cacheLocally && fileMeta.id) {
    // Der Zwischenspeicher bekommt die Bloecke so, wie sie aus dem Tresor kamen: schon verschluesselt,
    // im selben Format (IV + Kryptotext je Block). Bis 22.10.26 wurde hier alles noch einmal neu
    // verschluesselt, das kostete Rechenzeit und doppelten Speicher.
    await db.fileBlobs.put({ id: fileMeta.id, blob: new Blob(cipherParts) });
  }
  return blob;
}

/**
 * Bloecke einer Cloud-Datei in Reihenfolge entschluesselt liefern (async Iterator von Uint8Array).
 * Parallel vorgeholt, hoechstens 4 Bloecke gleichzeitig im Speicher. Fuer den Streaming-Download
 * und fuer das Schreiben direkt auf die Platte.
 */
export async function* decryptedChunksInOrder(authenticatedFetch, activeKey, fileMeta, onProgress) {
  const { uploadId, totalChunks, encryptionId } = fileMeta;
  if (!uploadId || !totalChunks || !encryptionId) throw new Error('NO_CLOUD_REFERENCE');
  const pool = createDownloadUrlPool(authenticatedFetch, uploadId, totalChunks);
  const ahead = [];
  let nextToFetch = 0;
  const refill = () => {
    while (ahead.length < DOWNLOAD_PARALLEL && nextToFetch < totalChunks) {
      const p = fetchChunk(pool, activeKey, fileMeta, nextToFetch++);
      p.catch(() => {}); // Fehler wird beim await unten geworfen, nicht als unbehandelt gemeldet
      ahead.push(p);
    }
  };
  refill();
  for (let i = 0; i < totalChunks; i++) {
    const { plain } = await ahead.shift();
    refill();
    if (onProgress) onProgress(i + 1, totalChunks);
    yield plain;
  }
}

/**
 * Stream a cloud file straight to disk via a FileSystemWritableFileStream (showSaveFilePicker).
 * Caller closes/aborts the writable. No local caching: big files must not bloat IndexedDB.
 */
export async function streamDecryptToWritable(authenticatedFetch, activeKey, fileMeta, writable, onProgress) {
  for await (const plain of decryptedChunksInOrder(authenticatedFetch, activeKey, fileMeta, onProgress)) {
    await writable.write(plain);
  }
}
